import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { AdminSpeedMeasureProgressBar } from '../AdminSpeedMeasureProgressBar';
import {
  DEFAULT_LTE_BAND_OPTIONS,
  LTE_METRIC_THRESHOLDS,
  extractApiError,
  isLteBandSupportedModemModel,
  metricQuality,
} from '../lte-band-section/LteBandSection';
import { apiService } from '../../services/api';
import { runAdminCheckerSpeedProbe } from '../../utils/adminProxySpeedStream';
import { createSpaLinkClickHandler } from '../../utils/spaLinkUtils';
import { translateModemRebootReason } from '../../utils/modemRebootUtils';
import '../proxy-table/proxy-table.css';
import './port-info-panel.css';

const DEFAULT_RF_BANDS = ['B1', 'B3', 'B7'];

/** Кэш техпрокси по порту — не дёргаем API на каждый poll/ремount */
const techProxyIdCache = new Map();

const SHORT_CITY = {
  'Санкт-Петербург': 'СПб',
  'санкт-петербург': 'СПб',
  'Saint Petersburg': 'СПб',
  SPB: 'СПб',
  Москва: 'МСК',
  москва: 'МСК',
  Moscow: 'МСК',
  MSK: 'МСК',
};

const RECONNECT_LABELS = {
  time: 'Время',
  link: 'Ссылка',
  time_link: 'Время + ссылка',
};

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const getModemNumberFromIp = (ip) => {
  if (!ip) return null;
  const parts = String(ip).split('.');
  if (parts.length === 4) return parts[2];
  return null;
};

const getPortModemNumber = (port) => {
  if (!port || typeof port !== 'object') return null;
  return getModemNumberFromIp(port.modem_internal_ip);
};

const getPortModemDisplayName = (port) => {
  const ipNumber = getPortModemNumber(port);
  return (ipNumber ? `№ ${ipNumber} ${port?.name || ''}` : (port?.name || 'unknown')).trim();
};

const getPortKrakenModemNumberForSimManager = (port) => {
  if (!port || typeof port !== 'object') return '';
  const accountingNumber = getPortModemNumber(port);
  if (accountingNumber != null && String(accountingNumber).trim() !== '') {
    return String(accountingNumber).trim();
  }
  if (port.kraken_modem_id != null && String(port.kraken_modem_id).trim() !== '') {
    return String(port.kraken_modem_id).trim();
  }
  if (port.kraken_id != null && String(port.kraken_id).trim() !== '') {
    return String(port.kraken_id).trim();
  }
  return '';
};

const getPortServerIdForSimManager = (port) => {
  if (!port || typeof port !== 'object') return '';
  if (port.server_id != null && String(port.server_id).trim() !== '') {
    return String(port.server_id).trim();
  }
  return '';
};

const isTechnicalTariffName = (raw) => {
  const value = String(raw || '').trim().toLowerCase();
  return value.includes('техническ') || value.includes('technical');
};

const extractProxiesList = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.proxies)) return payload.proxies;
  if (Array.isArray(payload)) return payload;
  return [];
};

const formatInfoSpeedMbpsPair = (dlMbps, ulMbps) => {
  const fmt = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(1)}`);
  return `${fmt(dlMbps)} / ${fmt(ulMbps)} Мб/с`;
};

const getStatusBadge = (port) => {
  const modemStatus = port?.modem_status || 'unknown';
  const isOffline = port?.modem_offline === true;
  if (isOffline) {
    return <span className="pxt-info__badge pxt-info__badge--gray">Выключен</span>;
  }
  if (modemStatus === 'working') {
    return <span className="pxt-info__badge pxt-info__badge--green">Рабочий</span>;
  }
  if (modemStatus === 'not_working' || modemStatus === 'unavailable') {
    return <span className="pxt-info__badge pxt-info__badge--red">Недоступен</span>;
  }
  return <span className="pxt-info__badge pxt-info__badge--gray">Неизвестно</span>;
};

const getLoadBadge = (loadRaw) => {
  const load = Number(loadRaw) || 0;
  let badgeColor = 'green';
  let badgeText = 'Min';
  if (load === 0 || load < 0.1) {
    badgeColor = 'gray';
    badgeText = 'Free';
  } else if (load > 99) {
    badgeColor = 'gray';
    badgeText = 'Load';
  } else if (load >= 71) {
    badgeColor = 'red';
    badgeText = 'Max';
  } else if (load >= 36) {
    badgeColor = 'yellow';
    badgeText = 'Med';
  }
  return (
    <span className="pxt-info__rotation-value">
      <span className={`pxt-info__badge pxt-info__badge--${badgeColor}`}>{badgeText}</span>
      <span className="pxt-info__line-value">{load.toFixed(0)}%</span>
    </span>
  );
};

const getOperatorBadge = (value) => {
  if (!value || value === '—') return <span className="pxt-info__line-value">—</span>;
  const v = String(value).toLowerCase();
  if (v.includes('мтс') || v === 'mts') {
    return <span className="pxt-info__badge" style={{ background: '#fee2e5', color: '#f33f4e' }}>{value}</span>;
  }
  if (v.includes('мегафон') || v === 'megafon') {
    return <span className="pxt-info__badge pxt-info__badge--green">{value}</span>;
  }
  if (v.includes('т2') || v.includes('теле2') || v === 'tele2') {
    return <span className="pxt-info__badge pxt-info__badge--gray">{value}</span>;
  }
  if (v.includes('билайн') || v === 'beeline') {
    return <span className="pxt-info__badge pxt-info__badge--yellow">{value}</span>;
  }
  return <span className="pxt-info__badge pxt-info__badge--gray">{value}</span>;
};

const getReconnectBadge = (type, interval) => {
  const rType = type || '';
  if (!rType) return <span className="pxt-info__line-value">—</span>;
  let badgeColor = 'green';
  if (rType === 'time_link') badgeColor = 'yellow';
  else if (rType === 'link') badgeColor = 'gray';
  else if (rType === 'time') badgeColor = 'yellow';
  const showInterval = rType !== 'link';
  return (
    <span className="pxt-info__rotation-value">
      <span className={`pxt-info__badge pxt-info__badge--${badgeColor}`}>
        {RECONNECT_LABELS[rType] || rType}
      </span>
      {showInterval ? (
        <span className="pxt-info__rotation-timer">{interval || 0}s</span>
      ) : null}
    </span>
  );
};

const CopyableValue = ({ value, label }) => {
  const text = String(value || '').trim();
  const canCopy = Boolean(text && text !== '—');

  const handleCopy = async () => {
    if (!canCopy) return;
    try {
      await navigator.clipboard.writeText(text);
      message.success(`${label} скопирован`);
    } catch {
      message.error('Не удалось скопировать');
    }
  };

  if (!canCopy) {
    return <span className="pxt-info__line-value">—</span>;
  }

  return (
    <button
      type="button"
      className="pxt-info__line-value pip-copy-value"
      onClick={handleCopy}
      aria-label={`Скопировать ${label} ${text}`}
      title="Нажмите, чтобы скопировать"
      tabIndex={0}
    >
      {text}
    </button>
  );
};

/**
 * Правая панель информации о сетевом порте (как pxt-info у прокси).
 */
const PortInfoPanel = ({
  port,
  onClose,
  onEdit,
  resolveOperator = null,
}) => {
  const navigate = useNavigate();

  const modemIdForRf = useMemo(() => {
    if (!port) return null;
    const n = Number(port.server_id ?? port.id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [port]);

  const modemModelForRf = useMemo(() => {
    if (!port) return '';
    return String(port.modem_type || port.modem_model || '').trim();
  }, [port]);

  const lteBandModelSupport = useMemo(
    () => isLteBandSupportedModemModel(modemModelForRf),
    [modemModelForRf]
  );

  const canRfMetrics = modemIdForRf != null && lteBandModelSupport !== false;
  const canRebootModem = modemIdForRf != null;
  const rfUnsupportedReason = lteBandModelSupport === false
    ? `LTE Band / RF только для ZTE. Модель: ${modemModelForRf || 'unknown'}`
    : null;

  const [speedBusy, setSpeedBusy] = useState(false);
  const [speedMeasureUi, setSpeedMeasureUi] = useState(() => ({ percent: 0, label: '' }));
  const [speedMaxMbps, setSpeedMaxMbps] = useState(() => ({ dl: null, ul: null }));
  const [speedAvgMbps, setSpeedAvgMbps] = useState(() => ({ dl: null, ul: null }));
  const [speedMeasureError, setSpeedMeasureError] = useState(null);
  const [techProxyId, setTechProxyId] = useState(null);
  const [techProxyError, setTechProxyError] = useState(null);
  const speedAbortRef = useRef(null);
  const techProxyResolvedForKeyRef = useRef('');

  const [rfBusy, setRfBusy] = useState(false);
  const [rfMetrics, setRfMetrics] = useState(null);
  const [rfError, setRfError] = useState(null);
  const [rfSelectedBands, setRfSelectedBands] = useState(DEFAULT_RF_BANDS);
  const [rfBandApplying, setRfBandApplying] = useState(false);
  const [rfBandError, setRfBandError] = useState(null);
  const rfApplyingRef = useRef(false);

  const [rebootBusy, setRebootBusy] = useState(false);

  const portServerId = port?.server_id;
  const portId = port?.id;
  const portConnectionId = port?.connection_id;
  const portKey = useMemo(() => {
    if (portServerId != null && portServerId !== '') return `server-${portServerId}`;
    if (portId == null) return '';
    return `${portId}-${portConnectionId || 'default'}`;
  }, [portServerId, portId, portConnectionId]);

  // Polling в NetworkPorts каждый раз даёт новый объект port — держим актуальный ref.
  const portRef = useRef(port);
  portRef.current = port;

  const rfBandOptions = useMemo(() => {
    const fromApi = rfMetrics?.available_bands;
    if (Array.isArray(fromApi) && fromApi.length) {
      return fromApi.map((item) => ({
        value: item.band,
        label: item.band,
        disabled: Boolean(item.disabled),
        disabledReason: item.disabled_reason || undefined,
      }));
    }
    return DEFAULT_LTE_BAND_OPTIONS;
  }, [rfMetrics]);

  useEffect(() => {
    return () => {
      if (speedAbortRef.current) {
        speedAbortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (speedAbortRef.current) {
      speedAbortRef.current.abort();
      speedAbortRef.current = null;
    }
    setSpeedBusy(false);
    setSpeedMeasureUi({ percent: 0, label: '' });
    setSpeedMaxMbps({ dl: null, ul: null });
    setSpeedAvgMbps({ dl: null, ul: null });
    setSpeedMeasureError(null);
  }, [portKey]);

  useEffect(() => {
    setRfBusy(false);
    setRfMetrics(null);
    setRfError(null);
    setRfSelectedBands(DEFAULT_RF_BANDS);
    setRfBandApplying(false);
    setRfBandError(null);
    rfApplyingRef.current = false;
    setRebootBusy(false);
  }, [modemIdForRf, portKey]);

  useEffect(() => {
    if (!rfMetrics || rfApplyingRef.current) return;
    if (Array.isArray(rfMetrics.bands_enabled) && rfMetrics.bands_enabled.length) {
      setRfSelectedBands(rfMetrics.bands_enabled.filter((b) => b !== 'B20'));
    } else if (Array.isArray(rfMetrics.allowed_lte_bands) && rfMetrics.allowed_lte_bands.length) {
      setRfSelectedBands(rfMetrics.allowed_lte_bands.filter((b) => b !== 'B20'));
    }
  }, [rfMetrics]);

  // Техпрокси: один раз на portKey, дальше из кэша. Без UI «Ищем…».
  useEffect(() => {
    if (!portKey) {
      setTechProxyId(null);
      setTechProxyError(null);
      techProxyResolvedForKeyRef.current = '';
      return undefined;
    }

    const cached = techProxyIdCache.get(portKey);
    if (cached) {
      setTechProxyId(cached.id);
      setTechProxyError(cached.error);
      techProxyResolvedForKeyRef.current = portKey;
      return undefined;
    }

    // Новый порт без кэша — сбрасываем id предыдущего, ищем один раз
    setTechProxyId(null);
    setTechProxyError(null);

    if (techProxyResolvedForKeyRef.current === portKey) {
      return undefined;
    }

    let ignore = false;

    const resolveTechProxy = async () => {
      try {
        const currentPort = portRef.current;
        const modemDisplay = getPortModemDisplayName(currentPort);
        const modemName = String(currentPort?.name || '').trim();
        const modemKeys = [...new Set([modemDisplay, modemName].filter(Boolean))];
        let foundId = null;

        for (const modem of modemKeys) {
          const res = await apiService.getProxiesPaginated({
            modem,
            limit: 200,
            offset: 0,
          });
          if (ignore) return;
          const list = extractProxiesList(res);
          const tech = list.find((item) => {
            const tariff = item?.tariff_plan || item?.tariff_plan_name || item?.flexible_tariff_name || '';
            return isTechnicalTariffName(tariff);
          });
          if (tech) {
            const id = Number(tech.proxy_id ?? tech.id);
            if (Number.isFinite(id) && id > 0) {
              foundId = id;
              break;
            }
          }
        }

        if (ignore) return;

        const next = foundId
          ? { id: foundId, error: null }
          : { id: null, error: 'Технический прокси на модеме не найден' };
        techProxyIdCache.set(portKey, next);
        techProxyResolvedForKeyRef.current = portKey;
        setTechProxyId(next.id);
        setTechProxyError(next.error);
      } catch (e) {
        if (ignore) return;
        const next = {
          id: null,
          error: e instanceof Error ? e.message : 'Ошибка поиска технического прокси',
        };
        techProxyIdCache.set(portKey, next);
        techProxyResolvedForKeyRef.current = portKey;
        setTechProxyId(null);
        setTechProxyError(next.error);
      }
    };

    void resolveTechProxy();
    return () => {
      ignore = true;
    };
  }, [portKey]);

  const handleSpeedMeasureClick = useCallback(async () => {
    if (!techProxyId) {
      setSpeedMeasureError(techProxyError || 'Нет технического прокси для замера');
      return;
    }
    if (speedAbortRef.current) {
      speedAbortRef.current.abort();
    }
    const ac = new AbortController();
    speedAbortRef.current = ac;
    setSpeedBusy(true);
    setSpeedMeasureError(null);
    setSpeedMeasureUi({ percent: 0, label: 'Подготовка…' });
    try {
      const result = await runAdminCheckerSpeedProbe(
        { proxy_id: techProxyId },
        {
          signal: ac.signal,
          onProgress: (ui) => setSpeedMeasureUi(ui),
        }
      );
      if (result.error) {
        setSpeedMeasureError(String(result.error));
        setSpeedMaxMbps({ dl: null, ul: null });
        setSpeedAvgMbps({ dl: null, ul: null });
      } else {
        setSpeedMaxMbps({
          dl: result.downloadMbpsMax ?? null,
          ul: result.uploadMbpsMax ?? null,
        });
        setSpeedAvgMbps({
          dl: result.downloadMbpsAvg ?? null,
          ul: result.uploadMbpsAvg ?? null,
        });
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      setSpeedMeasureError(e instanceof Error ? e.message : 'Ошибка замера');
      setSpeedMaxMbps({ dl: null, ul: null });
      setSpeedAvgMbps({ dl: null, ul: null });
    } finally {
      if (speedAbortRef.current === ac) {
        speedAbortRef.current = null;
      }
      setSpeedBusy(false);
    }
  }, [techProxyError, techProxyId]);

  const handleRfMetricsClick = useCallback(async () => {
    if (!canRfMetrics || rfApplyingRef.current) return;
    setRfBusy(true);
    setRfError(null);
    try {
      const res = await apiService.getModemRadioMetrics(modemIdForRf);
      const data = res?.data?.data || res?.data || null;
      setRfMetrics(data);
    } catch (e) {
      setRfError(extractApiError(e, 'Ошибка RF метрик'));
      setRfMetrics(null);
    } finally {
      setRfBusy(false);
    }
  }, [canRfMetrics, modemIdForRf]);

  const toggleRfBand = useCallback((band, disabled) => {
    if (disabled || rfApplyingRef.current) return;
    setRfSelectedBands((prev) => {
      if (prev.includes(band)) {
        if (prev.length <= 1) return prev;
        return prev.filter((b) => b !== band);
      }
      return [...prev, band];
    });
  }, []);

  const handleRfBandApply = useCallback(async () => {
    if (!canRfMetrics || !rfSelectedBands.length) return;
    rfApplyingRef.current = true;
    setRfBandApplying(true);
    setRfBandError(null);
    setRfError(null);
    try {
      const res = await apiService.setModemBandPolicy(modemIdForRf, rfSelectedBands);
      const data = res?.data?.data || res?.data || null;
      if (data) setRfMetrics(data);
      message.success('Band policy применён');
    } catch (e) {
      setRfBandError(extractApiError(e, 'Ошибка band policy'));
      try {
        rfApplyingRef.current = false;
        const res = await apiService.getModemRadioMetrics(modemIdForRf);
        const data = res?.data?.data || res?.data || null;
        if (data) setRfMetrics(data);
      } catch {
        // ignore secondary read
      }
    } finally {
      rfApplyingRef.current = false;
      setRfBandApplying(false);
    }
  }, [canRfMetrics, modemIdForRf, rfSelectedBands]);

  const handleRebootModemClick = useCallback(() => {
    if (!canRebootModem) {
      message.warning('Нет modem_id / server_id для перезагрузки');
      return;
    }
    const modemLabel = getPortModemDisplayName(port);
    Modal.confirm({
      title: 'Перезагрузка модема',
      content: `Перезагрузить модем «${modemLabel}»? Команда будет отправлена на хост подключения в фоне.`,
      okText: 'Перезагрузить',
      cancelText: 'Отмена',
      onOk: async () => {
        setRebootBusy(true);
        try {
          const response = await apiService.rebootModem(modemIdForRf);
          const queuedPayload = response?.data?.data || {};
          const taskId = queuedPayload?.task_id;
          message.success(
            taskId
              ? `Перезагрузка запущена (task ${taskId})`
              : 'Перезагрузка модема запущена'
          );
        } catch (e) {
          const rawDetail = e?.response?.data?.detail;
          const detail = (typeof rawDetail === 'string' ? translateModemRebootReason(rawDetail) : '')
            || extractApiError(e, 'Ошибка перезагрузки модема');
          message.error(detail);
        } finally {
          setRebootBusy(false);
        }
      },
    });
  }, [canRebootModem, modemIdForRf, port]);

  if (!port) return null;

  const server = port.connection_name || port.server_name || '—';
  const cityRaw = port.city || '';
  const city = SHORT_CITY[cityRaw] || cityRaw || '—';
  const modemLabel = getPortModemDisplayName(port);
  const proxyCount = Number(port.total_used || port.modem_total_proxies || 0);
  const load = port.overall_load_percentage !== undefined && port.overall_load_percentage !== null
    ? port.overall_load_percentage
    : (port.utilization !== undefined && port.utilization !== null ? port.utilization : 0);
  const performance = (() => {
    const raw = Number(port?.capacity_speed_mbps);
    return Number.isFinite(raw) && raw > 0 ? `${raw} Мб/с` : '—';
  })();
  const signal = (port.signal !== null && port.signal !== undefined) ? `${port.signal}/5` : '—';
  const bands = Array.isArray(port.allowed_lte_bands)
    ? port.allowed_lte_bands.map((b) => String(b || '').trim().toUpperCase()).filter(Boolean)
    : [];
  const bandsLabel = bands.length ? bands.join('+') : '—';
  const connInet = port.conn_inet;
  const internetLabel = connInet === true ? 'Подключен' : connInet === false ? 'Отключен' : 'Нет данных';
  const internetBadgeClass = connInet === true
    ? 'pxt-info__badge--green'
    : connInet === false
      ? 'pxt-info__badge--red'
      : 'pxt-info__badge--gray';

  const operatorRaw = typeof resolveOperator === 'function'
    ? resolveOperator(port.operator)
    : (port.operator || '');
  const operatorDisplay = !operatorRaw || operatorRaw === 'UNKNOWN' ? 'Неизвестно' : operatorRaw;

  const km = getPortKrakenModemNumberForSimManager(port);
  const sid = getPortServerIdForSimManager(port);
  const canSimLink = Boolean(km || sid);
  const simParams = new URLSearchParams();
  if (km) simParams.set('modem_number', km);
  if (sid) simParams.set('modem_server_id', sid);
  const simManagerPath = canSimLink ? `/sim-manager?${simParams.toString()}` : '';

  const proxiesModemPath = `/proxies?modem=${encodeURIComponent(modemLabel)}&page=1`;
  const osfpOn = Boolean(port.is_osfp);
  const osfpName = port.osfp_name || 'Android (Linux:2.6.x)';
  const canSpeedMeasure = Number.isFinite(Number(techProxyId)) && Number(techProxyId) > 0;

  return (
    <div
      className="pxt-info pip-panel"
      role="complementary"
      aria-label="Информация о сетевом порте"
    >
      <div className="pxt-info__header">
        <h2 className="pxt-info__title">Информация о порте</h2>
        <button
          type="button"
          className="pxt-info__close-btn"
          onClick={onClose}
          aria-label="Закрыть панель"
          tabIndex={0}
        >
          <CloseIcon />
        </button>
      </div>

      <div
        className="pxt-info__body"
        tabIndex={0}
        aria-label="Содержимое информации о порте"
      >
        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Статус</span>
            {getStatusBadge(port)}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Интернет</span>
            <span className={`pxt-info__badge ${internetBadgeClass}`}>{internetLabel}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Загрузка</span>
            {getLoadBadge(load)}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Скорость</span>
            <span className="pxt-info__badge pxt-info__badge--gray">{performance}</span>
          </div>
        </div>

        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Сервер</span>
            <span className="pxt-info__line-value">{server}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Город</span>
            <span className="pxt-info__line-value">{city}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Модем</span>
            <span className="pxt-info__line-value">{modemLabel}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Прокси</span>
            {proxyCount > 0 ? (
              <a
                href={proxiesModemPath}
                className="pip-link"
                onClick={createSpaLinkClickHandler(navigate, proxiesModemPath)}
                aria-label={`Открыть прокси модема ${modemLabel}`}
                tabIndex={0}
              >
                {proxyCount}
              </a>
            ) : (
              <span className="pxt-info__line-value">0</span>
            )}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Оператор</span>
            {canSimLink ? (
              <a
                href={simManagerPath}
                className="pip-link pip-link--badge"
                onClick={createSpaLinkClickHandler(navigate, simManagerPath)}
                aria-label={km ? `SIM-менеджер: SIM на модеме № ${km}` : 'SIM-менеджер'}
                tabIndex={0}
              >
                {getOperatorBadge(operatorDisplay)}
              </a>
            ) : (
              getOperatorBadge(operatorDisplay)
            )}
          </div>
        </div>

        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Сигнал</span>
            <span className="pxt-info__line-value">{signal}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Band</span>
            <span className="pxt-info__line-value">{bandsLabel}</span>
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Ротация</span>
            {getReconnectBadge(port.reconnect_type, port.reconnect_interval)}
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">Отпечаток ОС</span>
            {osfpOn ? (
              <span className="pxt-info__rotation-value">
                <span className="pxt-info__badge pxt-info__badge--green">On</span>
                <span className="pxt-info__line-value" title={osfpName}>
                  {osfpName.length > 18 ? `${osfpName.slice(0, 18)}…` : osfpName}
                </span>
              </span>
            ) : (
              <span className="pxt-info__badge pxt-info__badge--red">Off</span>
            )}
          </div>
        </div>

        <div className="pxt-info__section">
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">IMEI</span>
            <CopyableValue value={port.imei} label="IMEI" />
          </div>
          <div className="pxt-info__line">
            <span className="pxt-info__line-label">MAC</span>
            <CopyableValue value={port.mac_address} label="MAC" />
          </div>
        </div>

        <div className="pxt-info__section">
          <div className="pxt-info__speed-block" aria-label="RF метрики LTE модема">
            {rfError || rfUnsupportedReason ? (
              <div className="pxt-info__speed-error" role="alert">
                {rfError || rfUnsupportedReason}
              </div>
            ) : null}
            {rfMetrics ? (
              <div className="pxt-info__speed-results pxt-info__rf-results">
                <div className="pxt-info__line pxt-info__line--speed-result">
                  <span className="pxt-info__line-label">Band</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.wan_active_band || '—'}
                  </span>
                </div>
                <div className={`pxt-info__line pxt-info__line--speed-result pxt-info__rf-metric--${metricQuality('snr', rfMetrics.lte_snr)}`}>
                  <span className="pxt-info__line-label">SNR</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.lte_snr ?? '—'}
                    <span className="pxt-info__rf-hint">{LTE_METRIC_THRESHOLDS.snr.label}</span>
                  </span>
                </div>
                <div className={`pxt-info__line pxt-info__line--speed-result pxt-info__rf-metric--${metricQuality('rsrq', rfMetrics.lte_rsrq)}`}>
                  <span className="pxt-info__line-label">RSRQ</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.lte_rsrq ?? '—'}
                    <span className="pxt-info__rf-hint">{LTE_METRIC_THRESHOLDS.rsrq.label}</span>
                  </span>
                </div>
                <div className={`pxt-info__line pxt-info__line--speed-result pxt-info__rf-metric--${metricQuality('rsrp', rfMetrics.lte_rsrp)}`}>
                  <span className="pxt-info__line-label">RSRP</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {rfMetrics.lte_rsrp ?? '—'}
                    <span className="pxt-info__rf-hint">{LTE_METRIC_THRESHOLDS.rsrp.label}</span>
                  </span>
                </div>
              </div>
            ) : null}
            <button
              type="button"
              className="pxt-info__speed-btn"
              onClick={handleRfMetricsClick}
              disabled={!canRfMetrics || rfBandApplying || rfBusy}
              title={
                rfUnsupportedReason
                  || (!modemIdForRf ? 'Нет modem_id / server_id' : undefined)
              }
              aria-label="LTE Band"
              aria-busy={rfBusy}
              tabIndex={0}
            >
              {rfBusy ? 'Чтение RF…' : 'LTE Band'}
            </button>
            {canRfMetrics ? (
              <div className="pxt-info__rf-bands" aria-label="LTE band lock">
                <div className="pxt-info__rf-bands-label">Разрешённые band</div>
                <div className="pxt-info__rf-band-checks" role="group" aria-label="LTE bands">
                  {rfBandOptions.map((opt) => {
                    const checked = rfSelectedBands.includes(opt.value);
                    const disabled = Boolean(opt.disabled) || rfBandApplying || rfBusy;
                    return (
                      <label
                        key={opt.value}
                        className={`pxt-info__rf-band-check${disabled && opt.disabled ? ' pxt-info__rf-band-check--disabled' : ''}${checked ? ' pxt-info__rf-band-check--on' : ''}`}
                        title={opt.disabled ? (opt.disabledReason || 'Недоступно') : undefined}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled}
                          onChange={() => toggleRfBand(opt.value, Boolean(opt.disabled))}
                          aria-label={opt.label}
                        />
                        <span>{opt.label}</span>
                      </label>
                    );
                  })}
                </div>
                {rfBandError ? (
                  <div className="pxt-info__speed-error" role="alert">
                    {rfBandError}
                  </div>
                ) : null}
                <button
                  type="button"
                  className="pxt-info__speed-btn pxt-info__rf-band-apply"
                  onClick={handleRfBandApply}
                  disabled={rfBandApplying || rfBusy || !rfSelectedBands.length}
                  aria-label="Применить band"
                  tabIndex={0}
                >
                  {rfBandApplying ? 'Применение…' : 'Применить band'}
                </button>
              </div>
            ) : null}
          </div>

          <div className="pxt-info__speed-block" aria-label="Замер скорости через технический прокси">
            {!speedBusy && techProxyError && !canSpeedMeasure ? (
              <div className="pxt-info__speed-error" role="alert">
                {techProxyError}
              </div>
            ) : null}
            {!speedBusy && speedMeasureError ? (
              <div className="pxt-info__speed-error" role="alert">
                {speedMeasureError}
              </div>
            ) : null}
            {!speedBusy &&
            (speedMaxMbps.dl != null ||
              speedMaxMbps.ul != null ||
              speedAvgMbps.dl != null ||
              speedAvgMbps.ul != null) ? (
              <div className="pxt-info__speed-results">
                <div className="pxt-info__line pxt-info__line--speed-result">
                  <span className="pxt-info__line-label">Макс</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {formatInfoSpeedMbpsPair(speedMaxMbps.dl, speedMaxMbps.ul)}
                  </span>
                </div>
                <div className="pxt-info__line pxt-info__line--speed-result">
                  <span className="pxt-info__line-label">Средняя</span>
                  <span className="pxt-info__line-value pxt-info__speed-pair">
                    {formatInfoSpeedMbpsPair(speedAvgMbps.dl, speedAvgMbps.ul)}
                  </span>
                </div>
              </div>
            ) : null}
            {speedBusy ? (
              <div className="pxt-info__speed-progress">
                <AdminSpeedMeasureProgressBar
                  percent={speedMeasureUi.percent}
                  label={speedMeasureUi.label}
                />
              </div>
            ) : (
              <button
                type="button"
                className="pxt-info__speed-btn"
                onClick={handleSpeedMeasureClick}
                disabled={!canSpeedMeasure}
                title={
                  !canSpeedMeasure
                    ? (techProxyError || 'Нет технического прокси для замера')
                    : `Через технический прокси #${techProxyId}`
                }
                aria-label="Проверка скорости"
                tabIndex={0}
              >
                Проверка скорости
              </button>
            )}
          </div>

          <div className="pxt-info__speed-block" aria-label="Перезагрузка модема">
            <button
              type="button"
              className="pxt-info__speed-btn"
              onClick={handleRebootModemClick}
              disabled={!canRebootModem || rebootBusy}
              title={!canRebootModem ? 'Нет modem_id / server_id' : 'Перезагрузка модема через web UI'}
              aria-label="Перезагрузка"
              tabIndex={0}
            >
              {rebootBusy ? 'Перезагрузка…' : 'Перезагрузка'}
            </button>
          </div>
        </div>
      </div>

      <div className="pxt-info__actions">
        {onEdit ? (
          <button
            type="button"
            className="pxt-info__action-btn pxt-info__action-btn--edit"
            onClick={() => onEdit(port)}
            aria-label="Редактировать порт"
            tabIndex={0}
          >
            Редактировать
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default PortInfoPanel;
